/** About Us page content — /about-us */

import { pageRoute, appRoute, serviceRoute } from "./paths.js";

export const aboutPage = {
  documentTitle: "Taldel — About Us",
  hero: {
    badge: "About Taldel",
    titleSegments: [
      { text: "We build teams that ", highlight: null },
      { text: "ship", highlight: "primary" },
      { text: ", not ", highlight: null },
      { text: "slides", highlight: "secondary" },
      { text: ".", highlight: null },
    ],
    subtitle:
      "We started Taldel because most software teams are slowed down by process, not talent. So we built a leaner model — offshore engineers who plug directly into your roadmap, vetted by AI, accountable like an in-house team.",
    ctaPrimary: "Talk to us",
    ctaPrimaryHref: pageRoute("about-us", "contact"),
    ctaSecondary: "Explore services",
    ctaSecondaryHref: serviceRoute("web-development"),
    panel: {
      kicker: "Company pulse",
      title: "Less talk. More build.",
      body: "From first brief to production PRs — we stay close to your roadmap, tools, and rituals.",
      stats: [
        { value: "120+", label: "Products shipped" },
        { value: "6", label: "Capability lanes" },
        { value: "5d", label: "Typical deploy window" },
      ],
    },
  },
  story: {
    watermark: "Taldel",
    badge: "Our story",
    paragraph:
      "Founders and product leaders kept hitting the same wall: they needed senior engineering capacity without building a hiring machine. Agencies over-promised. Freelancers vanished mid-sprint. In-house recruiting took quarters they did not have. Taldel started as a quieter answer — AI-vetted engineers and squads that embed into your stack, share your timezone overlap, and own outcomes like an extension of your team. Not a body shop. A delivery partner that ships.",
    cta: "See how we work",
    ctaHref: pageRoute("about-us", "values"),
  },
  mission: {
    badge: "Mission & vision",
    titleSegments: [
      { text: "What we ", highlight: null },
      { text: "optimize", highlight: "primary" },
      { text: " for", highlight: null },
    ],
    cards: [
      {
        eyebrow: "Mission",
        title: "Make serious engineering accessible",
        body: "Give growing teams production-ready capacity — clear ownership, honest communication, and code that survives contact with real users.",
      },
      {
        eyebrow: "Vision",
        title: "The default partner for builders",
        body: "Be the team founders call when the roadmap is real and the hiring pipeline is not — offshore done properly, at the speed of product.",
      },
    ],
  },
  values: {
    badge: "How we work",
    titleSegments: [
      { text: "Principles we ", highlight: null },
      { text: "won't", highlight: "primary" },
      { text: " compromise", highlight: "secondary" },
    ],
    subtitle: "Culture is what happens when nobody is pitching. These are the defaults inside every Taldel engagement.",
    items: [
      {
        index: "01",
        title: "Ownership over optics",
        body: "We measure success in shipped outcomes, not status decks. If a lane is ours, we carry it.",
      },
      {
        index: "02",
        title: "Clarity beats jargon",
        body: "Straight updates, readable PRs, and decisions you can follow without translating buzzwords.",
      },
      {
        index: "03",
        title: "Embed, don't orbit",
        body: "Your tools, your rituals, your definition of done. We join the conversation — we don't invent a parallel one.",
      },
      {
        index: "04",
        title: "Quality under pressure",
        body: "Speed matters. So does code you can maintain next quarter. We refuse to trade one for the other quietly.",
      },
      {
        index: "05",
        title: "Human + AI, deliberately",
        body: "We use AI to vet and accelerate — never to hide. Humans still own judgment, taste, and accountability.",
      },
      {
        index: "06",
        title: "Long-game trust",
        body: "Most clients grow with us. That only works if week one feels like week twenty: consistent, calm, competent.",
      },
    ],
  },
  timeline: {
    badge: "The path",
    titleSegments: [
      { text: "From brief to ", highlight: null },
      { text: "velocity", highlight: "primary" },
    ],
    subtitle: "A simple arc we repeat across products and squads.",
    steps: [
      {
        step: "01",
        title: "Listen hard",
        body: "Stack, constraints, success metrics, and the political reality of shipping inside your org.",
      },
      {
        step: "02",
        title: "Match deliberately",
        body: "AI-vetted profiles, culture fit, and timezone overlap — then a human conversation before anyone starts.",
      },
      {
        step: "03",
        title: "Embed fast",
        body: "Access, rituals, and a first meaningful PR in the first week — not a month of orientation theater.",
      },
      {
        step: "04",
        title: "Raise the floor",
        body: "Steady cadence, clearer ownership, and a team you can scale or reshape as the product evolves.",
      },
    ],
  },
  cta: {
    badge: "Next step",
    titleSegments: [
      { text: "Ready to meet the ", highlight: null },
      { text: "team", highlight: "primary" },
      { text: " behind the work?", highlight: null },
    ],
    subtitle: "Tell us what you're building. We'll tell you how Taldel can help — honestly.",
    primary: "Start a conversation",
    primaryHref: pageRoute("about-us", "contact"),
    secondary: "See our people",
    secondaryHref: appRoute("team"),
  },
};

export function getAboutPage() {
  return aboutPage;
}

export function aboutRoute(section = "") {
  return pageRoute("about-us", section);
}
